import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { phraseMessages } from './translations'

export type Locale = 'en' | 'es' | 'pt-BR' | 'ja'

const messages: Record<Locale, Record<string, string>> = {
  en: {
    dashboard: 'Dashboard', schedule: 'Schedule', clients: 'Clients', messages: 'Messages',
    team: 'Team', finance: 'Finance', settings: 'Settings', invoices: 'Invoices', home: 'Home',
    newCleaning: 'New cleaning', newClient: 'New client', today: 'Today', signOut: 'Sign out',
    search: 'Search', loading: 'Loading your workspace…', noData: 'Nothing here yet', save: 'Save',
    cancel: 'Cancel', language: 'Language', account: 'Account', notifications: 'Notifications', plans: 'Plans & billing',
    ...phraseMessages.en,
  },
  es: {
    dashboard: 'Panel', schedule: 'Agenda', clients: 'Clientes', messages: 'Mensajes',
    team: 'Equipo', finance: 'Finanzas', settings: 'Configuración', invoices: 'Facturas', home: 'Inicio',
    newCleaning: 'Nueva limpieza', newClient: 'Nuevo cliente', today: 'Hoy', signOut: 'Cerrar sesión',
    search: 'Buscar', loading: 'Cargando tu espacio…', noData: 'Todavía no hay nada', save: 'Guardar',
    cancel: 'Cancelar', language: 'Idioma', account: 'Cuenta', notifications: 'Notificaciones', plans: 'Planes y facturación',
    ...phraseMessages.es,
  },
  'pt-BR': {
    dashboard: 'Painel', schedule: 'Agenda', clients: 'Clientes', messages: 'Mensagens',
    team: 'Equipe', finance: 'Financeiro', settings: 'Configurações', invoices: 'Faturas', home: 'Início',
    newCleaning: 'Nova limpeza', newClient: 'Novo cliente', today: 'Hoje', signOut: 'Sair',
    search: 'Buscar', loading: 'Carregando sua empresa…', noData: 'Nada por aqui ainda', save: 'Salvar',
    cancel: 'Cancelar', language: 'Idioma', account: 'Conta', notifications: 'Notificações', plans: 'Planos e cobrança',
    ...phraseMessages['pt-BR'],
  },
  ja: {
    dashboard: 'ダッシュボード', schedule: 'スケジュール', clients: '顧客', messages: 'メッセージ',
    team: 'チーム', finance: '財務', settings: '設定', invoices: '請求書', home: 'ホーム',
    newCleaning: '清掃を追加', newClient: '顧客を追加', today: '今日', signOut: 'ログアウト',
    search: '検索', loading: 'ワークスペースを読み込み中…', noData: 'まだデータがありません', save: '保存',
    cancel: 'キャンセル', language: '言語', account: 'アカウント', notifications: '通知', plans: 'プランと請求',
    ...phraseMessages.ja,
  },
}

interface I18nValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, values?: Record<string, string | number>) => string
}

export type Translator = I18nValue['t']

const I18nContext = createContext<I18nValue | null>(null)

function initialLocale(): Locale {
  const saved = localStorage.getItem('tidyping-locale') as Locale | null
  if (saved && saved in messages) return saved
  const browser = navigator.language
  if (browser.startsWith('pt')) return 'pt-BR'
  if (browser.startsWith('es')) return 'es'
  if (browser.startsWith('ja')) return 'ja'
  return 'en'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, updateLocale] = useState<Locale>(initialLocale)
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])
  const value = useMemo<I18nValue>(() => ({
    locale,
    setLocale(next) {
      localStorage.setItem('tidyping-locale', next)
      document.documentElement.lang = next
      updateLocale(next)
    },
    t: (key, values) => {
      let lookupKey = key
      let lookupValues = values
      const clientLimit = key.match(/^(.+) supports up to (\d+) active clients$/)
      const teamLimit = key.match(/^(.+) supports up to (\d+) active team members$/)
      const invitedEmail = key.match(/^Sign in with the invited work email: (.+)$/)
      if (clientLimit) {
        lookupKey = '{plan} supports up to {count} active clients'
        lookupValues = { plan: clientLimit[1] ?? '', count: clientLimit[2] ?? '' }
      } else if (teamLimit) {
        lookupKey = '{plan} supports up to {count} active team members'
        lookupValues = { plan: teamLimit[1] ?? '', count: teamLimit[2] ?? '' }
      } else if (invitedEmail) {
        lookupKey = 'Sign in with the invited work email: {email}'
        lookupValues = { email: invitedEmail[1] ?? '' }
      }
      const template = messages[locale][lookupKey] ?? messages.en[lookupKey] ?? key
      if (!lookupValues) return template
      return template.replace(/\{(\w+)\}/g, (match, name: string) => String(lookupValues[name] ?? match))
    },
  }), [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside I18nProvider')
  return value
}

export function T({ children, values }: { children: string; values?: Record<string, string | number> }) {
  const { t } = useI18n()
  const leading = children.match(/^\s*/)?.[0] ?? ''
  const trailing = children.match(/\s*$/)?.[0] ?? ''
  const key = children.trim()
  return <>{leading}{key ? t(key, values) : ''}{trailing}</>
}

const storedLocaleTags: Record<Locale, string> = {
  en: 'en-US',
  es: 'es-US',
  'pt-BR': 'pt-BR',
  ja: 'ja-JP',
}

function localizeStoredSlot(value: string, locale: Locale) {
  const match = value.match(/^(Sun|Mon|Tue|Wed|Thu|Fri|Sat), (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2}), (\d{1,2}):(\d{2}) (AM|PM)$/)
  if (!match) return value

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const weekday = weekdays.indexOf(match[1] ?? '')
  const month = months.indexOf(match[2] ?? '')
  const day = Number(match[3] ?? 0)
  const sourceHour = Number(match[4] ?? 0)
  const minute = Number(match[5] ?? 0)
  const hour = sourceHour % 12 + (match[6] === 'PM' ? 12 : 0)
  const currentYear = new Date().getFullYear()

  for (let offset = 0; offset <= 14; offset += 1) {
    const year = currentYear + (offset % 2 === 0 ? offset / 2 : -Math.ceil(offset / 2))
    const candidate = new Date(year, month, day, hour, minute)
    if (candidate.getMonth() !== month || candidate.getDate() !== day || candidate.getDay() !== weekday) continue
    return new Intl.DateTimeFormat(storedLocaleTags[locale], {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(candidate)
  }
  return value
}

/**
 * Localizes system-authored text stored in Supabase while leaving client and
 * business chat messages untouched. Older rows contain rendered English text,
 * so known variable portions are recovered before looking up the translation.
 */
export function translateStoredText(t: Translator, locale: Locale, text: string) {
  const direct = t(text)
  if (direct !== text) return direct
  const localizedSlot = localizeStoredSlot(text, locale)
  if (localizedSlot !== text) return localizedSlot

  const patterns: Array<[RegExp, string, (match: RegExpMatchArray) => Record<string, string | number>]> = [
    [/^New message from (.+)$/, 'New message from {name}', (match) => ({ name: match[1] ?? '' })],
    [/^A cleaning was proposed for (.+)\. Business approval is required before the client is notified\.$/, 'A cleaning was proposed for {slot}. Business approval is required before the client is notified.', (match) => ({ slot: localizeStoredSlot(match[1] ?? '', locale) })],
    [/^The business requested a new time: (.+)\. The client must confirm the change\.$/, 'The business requested a new time: {slot}. The client must confirm the change.', (match) => ({ slot: localizeStoredSlot(match[1] ?? '', locale) })],
    [/^Please confirm (.+)\.$/, 'Please confirm {slot}.', (match) => ({ slot: localizeStoredSlot(match[1] ?? '', locale) })],
    [/^The cleaning time changed to (.+)\. Both confirmations were reset\.$/, 'The cleaning time changed to {slot}. Both confirmations were reset.', (match) => ({ slot: localizeStoredSlot(match[1] ?? '', locale) })],
    [/^The client selected (.+)\. Business approval is needed\.$/, 'The client selected {slot}. Business approval is needed.', (match) => ({ slot: localizeStoredSlot(match[1] ?? '', locale) })],
    [/^(\d+) openings suggested$/, '{count} openings suggested', (match) => ({ count: Number(match[1] ?? 0) })],
    [/^(\d+) new openings$/, '{count} new openings', (match) => ({ count: Number(match[1] ?? 0) })],
    [/^Sent (\d+) photos or videos\.$/, 'Sent {count} photos or videos.', (match) => ({ count: Number(match[1] ?? 0) })],
    [/^Invoice (.+) is ready in TidyPing\.$/, 'Invoice {number} is ready in TidyPing.', (match) => ({ number: match[1] ?? '' })],
    [/^(.+) is finished\. Open TidyPing to view the details\.$/, '{service} is finished. Open TidyPing to view the details.', (match) => ({ service: match[1] ?? '' })],
    [/^(.+) was marked paid by the cleaning company\.$/, '{service} was marked paid by the cleaning company.', (match) => ({ service: match[1] ?? '' })],
    [/^The business cancelled this cleaning\. Reason: (.+)$/, 'The business cancelled this cleaning. Reason: {reason}', (match) => ({ reason: match[1] ?? '' })],
    [/^The client cancelled this cleaning\. Reason: (.+)$/, 'The client cancelled this cleaning. Reason: {reason}', (match) => ({ reason: match[1] ?? '' })],
  ]

  for (const [pattern, key, values] of patterns) {
    const match = text.match(pattern)
    if (match) return t(key, values(match))
  }
  return text
}
